import { useMemo, useRef } from "react";
import Accordion from "../Accordion";
import AnchorBar from "../AnchorBar";
import Presentation from "../Presentation";
import { useIsMobile } from '../Layout/useIsMobile';
import Slider from "../Slider";

import './PageProjectBlissimRGPD.scss';

export const PageProjectBlissimRGPD = () => {
  const isMobile = useIsMobile();
  const contextRef = useRef(null);
  const resultsRef = useRef(null);
  const searchRef = useRef(null);
  const conclusionRef = useRef(null);
  const sliderItems = useMemo(() => [
    {
      classSuffix: "blissim-gift",
      image: "/images/Blissim/Gift/presentation-mobile.png",
      subtitle: "REFONTE WEB",
      title: "Blissim - Gift",
      url: "/projet/blissim-gift"
    },
    {
      classSuffix: "indigo-neo",
      image: "/images/IndigoNeo/presentation-mobile.png",
      subtitle: "REFONTE APP / WEB",
      title: "Indigo Neo",
      url: "/projet/indigo-neo"
    },
    {
      classSuffix: "o-revoir",
      image: "/images/ORevoir/presentation-mobile.png",
      subtitle: "BUSINESS PLAN",
      title: "ô revoir",
      url: "/projet/o-revoir"
    }
  ], []);

  const anchors = useMemo(() => ([
    { id: "context", ref: contextRef, title: "Contexte" },
    { id: "search", ref: searchRef, title: "Recherches" },
    { id: "results", ref: resultsRef, title: "Résultats" },
    { id: "conclusion", ref: conclusionRef, title: "Conclusion" },
  ]), []);

  const problemTitle = "Problème à résoudre";
  const problemText = "La précédente expérience d’inscription et de gestion du compte n’était pas entièrement alignée avec les nouvelles recommandations RGPD.<br />Challenge UX : intégrer les contraintes réglementaires sans alourdir le parcours ni générer de méfiance, pour éviter les abandons ou la frustration.";

  return (
    <div className="page-project blissim-rgpd">
      <Presentation
        alt="Blissim RGPD icon"
        classSuffix="blissim-rgpd"
        image={`/images/Blissim/RGPD/presentation${isMobile ? '-mobile' : '-desktop'}.png`}
        title="Blissim - RGPD"
        subtitle="PRODUCT DESIGNER / REFONTE"
      />

      <AnchorBar anchors={anchors} />

      <section className="anchor-section">
        <h2 ref={contextRef} id="discover" className="anchor--title">Contexte</h2>
        <hr className="anchor--separator" />

        <div className="discover-information">
          <div id="target" className="discover-information-wrapper">
            <h3 className="discover-information--title">Objectifs</h3>
            <p className="discover-information--description">
              Mettre le parcours d'inscription et de connexion en conformité avec la RGPD, tout en garantissant une expérience fluide 
              pour les utilisateurs. L’enjeu était double : respecter strictement les obligations légales sur la collecte des
              consentements et ne pas nuire à la conversion ni à la perception de la marque.
            </p>
          </div>

          <div id="tools" className="discover-information-wrapper">
            <h3 className="discover-information--title">Outils</h3>
            <div className="discover-information--list">
              <div className="discover-information-list-item">
                <img className="icon-tool" src="/images/icon-figma.svg" alt="icon Figma" />
                <span className="icon-tool-title">Figma</span>
              </div>
            </div>
          </div>

          <div id="target" className="discover-information-wrapper">
            <h3 className="discover-information--title">Durée</h3>
            <p className="discover-information--description">
              ~ 2 semaines
            </p>
          </div>
        </div>

        <div id="problem" className="discover-information-wrapper">
          <Accordion
            accordionName="accordion--discover-information__problem"
            content={problemText}
            label={problemTitle}
          >
            <>
              <h3 className="discover-information--title">{problemTitle}</h3>
              <p className="discover-information--description__full-size" dangerouslySetInnerHTML={{ __html: problemText }} />
            </>
          </Accordion>
        </div>
      </section>

      <section className="anchor-section">
        <h2 ref={searchRef} id="define" className="anchor--title">Analyse & Recherches</h2>
        <hr className="anchor--separator" />

        <div>
          <h3 className="define--title">Besoins sur le projet :</h3>

          <div className="define--block">
            <div>
              <h4 className="define-block--subtitle">🧑‍💻 Côté utilisateur</h4>
              <ul>
                <li>Ne pas rendre l'expérience trop intrusive ou stressante.</li>
                <li>Assurer une totale transparence.</li>
                <li>Maintenir la fluidité du parcours.</li>
              </ul>
            </div>

            <div>
              <h4 className="define-block--subtitle">💼 Côté business</h4>
              <ul>
                <li>Objectif prioritaire : conserver un maximum de consentements marketing tout en se mettant en conformité, afin de ne pas perdre la base adressable pour les campagnes email et CRM.</li>
                <li>Éviter un scénario où une présentation trop anxiogène ou contraignante ferait fuir les utilisateurs dès l'inscription ou lors de la mise à jour RGPD.</li>
              </ul>
            </div>
          </div>

          <h3 className="define--title">Benchmark</h3>
          <div className="generic-flex-wrapper">
            <div className="define--benchmark-card define--benchmark-card__blue">
              <div className="define--benchmark-card__text">
                <h4 className="define--benchmark-card__title"><strong>Carrefour</strong> - à la connexion</h4>
                <p className="define--benchmark-card__subtitle">🔍 Observations :</p>
                <p>
                  Carrefour propose une modale directement à la connexion du compte.
                  L’approche est frontale mais claire, en mettant en avant une personnalisation
                  des contenus en échange du consentement.
                </p>
                <p className="define--benchmark-card__subtitle">✅ Impact UX :</p>
                <p>
                  Une fois connecté, l’utilisateur est plus susceptible de donner son consentement
                  car il est déjà engagé dans une démarche.
                </p>
              </div>
              <img alt="Carrefour" className="define--benchmark-card__image-first" src="/images/Blissim/RGPD/carrefour.png" />
            </div>

            <div className="define--benchmark-card define--benchmark-card__black">
              <div className="define--benchmark-card__text">
                <h4 className="define--benchmark-card__title"><strong>Sephora</strong> - à la connexion</h4>
                <p className="define--benchmark-card__subtitle">🔍 Observations :</p>
                <p>
                  Sephora intègre la gestion des préférences de communication dans un parcours de
                  création de compte en 4 étapes. L’approche est progressive, claire, et rassurante.
                </p>
                <p className="define--benchmark-card__subtitle">✅ Impact UX :</p>
                <p>
                  La division en étapes simplifie la charge cognitive. Chaque étape est légère et
                  guidée.
                </p>
              </div>
              <img alt="Sephora" className="define--benchmark-card__image-second" src="/images/Blissim/RGPD/sephora.png" />
            </div>
          </div>
        </div>
      </section>

      <section className="anchor-section">
        <h2 ref={resultsRef} id="design" className="anchor--title">Résultats</h2>
        <hr className="anchor--separator" />

        <div className="generic-flex-wrapper design-wrapper design-wrapper__with-subtitle">
          <div className="design-container design-container__half-screen">
            <h4>À la connexion - Mobile</h4>
            <p>Modale RGPD affichée à la reconnexion utilisateur</p>

            <div className="generic-flex-wrapper design--image-container">
              <img alt="RGPD Mobile connexion" className="design--image design--image__first" src="/images/Blissim/RGPD/RGPD-mobile-connect.png" />
            </div>
          </div>
          <div className="design-container design-container__half-screen">
            <h4>À la connexion - Desktop</h4>
            <p>Modale RGPD affichée à la reconnexion utilisateur</p>

            <div className="generic-flex-wrapper design--image-container">
              <img alt="RGPD Desktop connexion" className="design--image design--image__second" src="/images/Blissim/RGPD/RGPD-desktop-connect.png" />
            </div>
          </div>
        </div>

        <div className="generic-flex-wrapper design-wrapper">
          <div className="design-container design-container__half-screen">
            <h4>Avant - Inscription</h4>

            <div className="generic-flex-wrapper design--image-container">
              <img alt="RGPD Pré inscription" className="design--image design--image__third" src="/images/Blissim/RGPD/RGPD-before-sign-up.png" />
            </div>
          </div>
          <div className="design-container design-container__half-screen">
            <h4>Après - Inscription</h4>

            <div className="generic-flex-wrapper design--image-container">
              <img alt="RGPD Post inscription" className="design--image design--image__fourth" src="/images/Blissim/RGPD/RGPD-after-sign-up.png" />
            </div>
          </div>
        </div>
      </section>

      <section className="anchor-section">
        <h2 ref={conclusionRef} id="realize" className="anchor--title">Conclusion</h2>
        <hr className="anchor--separator" />

        <div className="generic-flex-wrapper">
          <div className="define--benchmark-card define--benchmark-card__yellow">
            <div className="define--benchmark-card__text">
              <h4 className="define--benchmark-card__title">Une mise en conformité RGPD</h4>
              <p>
                Ce projet visait avant tout à répondre aux obligations légales en matière de collecte de consentement,
                tout en conservant une approche orientée utilisateur.
              </p>
              <p>
                Deux actions ont été menées : la mise à jour des champs à l’inscription, suivie du déploiement d’une
                modale de consentement à la connexion.
              </p>
            </div>
          </div>

          <div className="define--benchmark-card define--benchmark-card__green">
            <div className="define--benchmark-card__text">
              <h4 className="define--benchmark-card__title">Résultats clés</h4>
              <p>En juin 2025, le déploiement de la nouvelle modale a permis de collecter 79k consentements supplémentaires, représentant 51 % des opt-ins.</p>
              <p>
                👉 En combinant clarté du message, intégration fluide dans le parcours et transparence sur l’usage des données, la marque a non seulement
                respecté la législation, mais aussi amélioré ses performances de collecte.
              </p>
            </div>
          </div>
        </div>
      </section>

      <Slider sliderItems={sliderItems} />
    </div>
  );
};

export default PageProjectBlissimRGPD;